import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAuthOptional } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getConsoleBuffer } from "@/lib/console-buffer";
import { captureScreenshot } from "@/lib/screenshot";
import { computeFingerprint, deriveModule, detectEnvironment, getSessionId } from "@/lib/report-context";
import { ChevronDown, Loader2 } from "lucide-react";

export type ReportErrorContext = {
  error?: Error | unknown;
  errorMessage?: string;
  errorName?: string;
  errorStack?: string;
  componentStack?: string;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: ReportErrorContext;
}

const guestSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
});

function extractError(ctx: ReportErrorContext) {
  if (ctx.errorMessage) {
    return { errorMessage: ctx.errorMessage, errorName: ctx.errorName, errorStack: ctx.errorStack };
  }
  const e = ctx.error;
  if (e instanceof Error) return { errorMessage: e.message, errorName: e.name, errorStack: e.stack };
  return { errorMessage: typeof e === "string" ? e : "Unknown error", errorName: undefined, errorStack: undefined };
}

export function ReportErrorDialog({ open, onOpenChange, context }: Props) {
  const user = useAuthOptional()?.user ?? null;
  const [userMessage, setUserMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // Screenshot is opt-in: default OFF. html2canvas is only invoked when the
  // user explicitly toggles this on before submit.
  const [attachScreenshot, setAttachScreenshot] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ id: string } | null>(null);

  const err = useMemo(() => extractError(context), [context]);

  useEffect(() => {
    if (open) {
      setSubmitted(null);
      setUserMessage("");
      setName("");
      setEmail("");
      setAttachScreenshot(false);
      setShowDetails(false);
    }
  }, [open]);

  const diagnostic = useMemo(() => {
    if (typeof window === "undefined") return null;
    return {
      route: window.location.pathname,
      url: window.location.href,
      userAgent: navigator.userAgent,
      locale: navigator.language,
      screen: `${window.screen.width}x${window.screen.height} @${window.devicePixelRatio}x`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      environment: detectEnvironment(),
      module: deriveModule(window.location.pathname),
      sessionId: getSessionId(),
    };
  }, [open]);

  async function handleSubmit() {
    if (!user) {
      const parsed = guestSchema.safeParse({ name, email });
      if (!parsed.success) {
        toast.error(parsed.error.errors[0]?.message ?? "Please complete contact details");
        return;
      }
    }
    setSubmitting(true);
    try {
      let screenshotBase64: string | undefined;
      if (attachScreenshot) {
        await new Promise((r) => requestAnimationFrame(() => r(null)));
        const shot = await captureScreenshot();
        if (shot) screenshotBase64 = shot;
      }

      const fingerprint = await computeFingerprint({
        errorName: err.errorName,
        errorMessage: err.errorMessage,
        errorStack: err.errorStack,
        module: diagnostic?.module ?? "unknown",
      });

      const payload = {
        errorMessage: err.errorMessage,
        errorName: err.errorName,
        errorStack: err.errorStack,
        componentStack: context.componentStack,
        userMessage: userMessage || undefined,
        route: diagnostic?.route,
        url: diagnostic?.url,
        module: diagnostic?.module,
        environment: diagnostic?.environment,
        sessionId: diagnostic?.sessionId,
        fingerprint,
        reporterName: user ? undefined : name,
        reporterEmail: user ? undefined : email,
        device: diagnostic?.userAgent,
        browser: diagnostic?.userAgent,
        screenResolution: diagnostic?.screen,
        locale: diagnostic?.locale,
        appVersion: (import.meta as { env?: Record<string, string> }).env?.VITE_APP_VERSION ?? "dev",
        consoleBuffer: getConsoleBuffer(),
        diagnostic: {
          viewport: diagnostic?.viewport,
          online: typeof navigator !== "undefined" ? navigator.onLine : null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          referrer: typeof document !== "undefined" ? document.referrer : "",
        },
        screenshotBase64,
      };

      const { data, error } = await supabase.functions.invoke("report-error", { body: payload });
      if (error) throw error;
      if (!data?.ok) throw new Error("Submission failed");
      setSubmitted({ id: data.bugReportId });
      toast.success("Thanks — your report has been sent to Support.");
    } catch (e) {
      console.error("report submit failed", e);
      toast.error("Could not send report. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden">
        {submitted ? (
          <>
            <DialogHeader>
              <DialogTitle>Report sent</DialogTitle>
              <DialogDescription>
                Reference <span className="font-mono">BUG-{submitted.id.slice(0, 8)}</span>. Our team has been notified.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              {user && (
                <Button variant="outline" asChild>
                  <a href={`/app/support/${submitted.id}`}>View in Support</a>
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Report this issue to Support</DialogTitle>
              <DialogDescription>
                We&apos;ll attach diagnostic info to help us reproduce and fix it.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 min-w-0">
              <div className="rounded-md border bg-muted/40 p-3 text-sm min-w-0 overflow-hidden">
                <div className="font-medium break-words line-clamp-2">{err.errorMessage}</div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="userMessage">What were you doing? (optional)</Label>
                <Textarea
                  id="userMessage" rows={3} maxLength={1000}
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  placeholder="e.g. Tried to save a new retailer name…"
                />
              </div>

              {user ? (
                <p className="text-sm text-muted-foreground">
                  Reporting as <span className="font-medium">{user.email}</span>
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="rname">Name</Label>
                    <Input id="rname" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="remail">Email</Label>
                    <Input id="remail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="shot">Attach screenshot of this screen</Label>
                  <p className="text-xs text-muted-foreground">Off by default — turn on to include.</p>
                </div>
                <Switch id="shot" checked={attachScreenshot} onCheckedChange={setAttachScreenshot} />
              </div>

              <Collapsible open={showDetails} onOpenChange={setShowDetails}>
                <CollapsibleTrigger asChild>
                  <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                    <ChevronDown className={`h-3 w-3 transition-transform ${showDetails ? "rotate-180" : ""}`} />
                    Diagnostic details
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-muted/40 p-2 text-[10px] leading-tight">
{JSON.stringify(diagnostic, null, 2)}
{err.errorStack ? `\n\nStack:\n${err.errorStack.slice(0, 1200)}` : ""}
                  </pre>
                </CollapsibleContent>
              </Collapsible>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send report
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
